import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useComparison } from '../contexts/ComparisonContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { usePermissions } from '../hooks/usePermissions';
import useSystemStatus from '../hooks/useSystemStatus';

function UserMenu({ onCloseMobileMenu }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const menuRef = useRef(null);
  const { user, logout, updateUserData } = useAuth();
  const { getComparisonCount } = useComparison();
  const navigate = useNavigate();
  const location = useLocation();
  const [favoritesCount, setFavoritesCount] = useState(0);
  const { canAccessAdmin, canCreateProperties, userProfile } = usePermissions();
  const { isMasterUser } = useSystemStatus();
  
  // Função para formatar telefone brasileiro
  const formatPhone = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Limita a 11 dígitos (DDD + 9 dígitos)
    const limitedNumbers = numbers.slice(0, 11);
    
    // Aplica a formatação
    if (limitedNumbers.length <= 2) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2)}`;
    } else if (limitedNumbers.length <= 11) {
      return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
    }
    
    return limitedNumbers;
  };

  // Função para lidar com mudança no telefone
  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  // Inicializa os campos quando o usuário é carregado
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      // Formata o telefone se existir
      const phoneValue = user.phone || '';
      setPhone(phoneValue ? formatPhone(phoneValue) : '');
    }
  }, [user]);

  // Carrega a contagem de favoritos
  useEffect(() => {
    if (!user) {
      // Carrega contagem do localStorage para usuários não logados
      const updateLocalFavoritesCount = () => {
        const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
        const localCondo = JSON.parse(localStorage.getItem('localCondominiumFavorites') || '[]');
        setFavoritesCount(localFavorites.length + localCondo.length);
      };

      updateLocalFavoritesCount();

      // Listener para mudanças no localStorage
      const interval = setInterval(updateLocalFavoritesCount, 1000);
      window.addEventListener('storage', updateLocalFavoritesCount);

      return () => {
        clearInterval(interval);
        window.removeEventListener('storage', updateLocalFavoritesCount);
      };
    }

    const db = getFirestore();
    const favoritesQuery = query(
      collection(db, 'favorites'),
      where('userId', '==', user.uid)
    );
    let cancelled = false;
    (async () => {
      try {
        const snapshot = await getDocs(favoritesQuery);
        if (!cancelled) setFavoritesCount(snapshot.docs.length);
      } catch (error) {
        if (!cancelled) setFavoritesCount(0);
        console.error('Erro ao carregar favoritos:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Fecha o menu quando clica fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const handleFavorites = () => {
    navigate('/favoritos');
    setIsOpen(false);
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  const handleComparison = () => {
    navigate('/comparacao');
    setIsOpen(false);
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  const handleUpdateProfile = () => {
    setShowUpdateForm(true);
    setIsOpen(false);
  };

  const handleSaveProfile = async () => {
    try {
      // Remove formatação do telefone antes de salvar
      const phoneNumbers = phone.replace(/\D/g, '');
      
      await updateUserData(user.uid, {
        name: name,
        phone: phoneNumbers
      });
      setShowUpdateForm(false);
      alert('Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      alert('Erro ao atualizar perfil');
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateForm(false);
    setName(user.name || '');
    const phoneValue = user.phone || '';
    setPhone(phoneValue ? formatPhone(phoneValue) : '');
  };

  const handleAddProperty = () => {
    navigate('/cadastrar');
    setIsOpen(false);
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  const handleAdminArea = () => {
    navigate('/admin');
    setIsOpen(false);
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  const handleMasterArea = () => {
    navigate('/master');
    setIsOpen(false);
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Botão do ícone de perfil */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary border-2 border-primary text-primary hover:bg-secondary-hover transition-colors"
        title="Menu do usuário"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          fill="currentColor" 
          viewBox="0 0 24 24" 
          className="w-6 h-6"
        >
          <path 
            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" 
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* Informações do usuário */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-secondary border-2 border-primary flex items-center justify-center">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="currentColor" 
                    viewBox="0 0 24 24" 
                    className="w-6 h-6 text-primary"
                  >
                    <path 
                      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" 
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user ? (user?.isAdmin ? 'Administrador' : (userProfile?.name || user?.name || 'Usuário')) : 'Visitante'}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {user ? user?.email : 'Não conectado'}
                </p>
              </div>
            </div>
          </div>

          {/* Ações do menu */}
          <div className="py-1">
            {/* Área do Master - Para usuário master */}
            {user && isMasterUser(user) && (
              <button
                onClick={handleMasterArea}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors relative"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth={1.5} 
                  stroke="currentColor" 
                  className="w-5 h-5 mr-3 text-gray-400"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" 
                  />
                </svg>
                Área do Master
              </button>
            )}

            {/* Área Admin - Para usuários com permissão de acesso admin */}
            {user && canAccessAdmin() && (
              <button
                onClick={handleAdminArea}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors relative"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth={1.5} 
                  stroke="currentColor" 
                  className="w-5 h-5 mr-3 text-gray-400"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" 
                  />
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                  />
                </svg>
                {user?.isAdmin ? 'Área Admin' : 'Área do Corretor'}
              </button>
            )}

            {/* Adicionar Propriedade - Para usuários com permissão de criar propriedades */}
            {user && canCreateProperties() && (
              <button
                onClick={handleAddProperty}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors relative"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth={1.5} 
                  stroke="currentColor" 
                  className="w-5 h-5 mr-3 text-gray-400"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M12 4.5v15m7.5-7.5h-15" 
                  />
                </svg>
                Adicionar Propriedade
              </button>
            )}

            <button
              onClick={handleFavorites}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors relative"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={1.5} 
                stroke="currentColor" 
                className="w-5 h-5 mr-3 text-gray-400"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" 
                />
              </svg>
              Favoritos
              {favoritesCount > 0 && (
                <span className="ml-auto bg-black text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {favoritesCount}
                </span>
              )}
            </button>

            <button
              onClick={handleComparison}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors relative"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={1.5} 
                stroke="currentColor" 
                className="w-5 h-5 mr-3 text-gray-400"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" 
                />
              </svg>
              Comparação
              {getComparisonCount() > 0 && (
                <span className="ml-auto bg-black text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                  {getComparisonCount()}
                </span>
              )}
            </button>

            {user && !canAccessAdmin() && (
              <button
                onClick={handleUpdateProfile}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth={1.5} 
                  stroke="currentColor" 
                  className="w-5 h-5 mr-3 text-gray-400"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" 
                  />
                </svg>
                Atualizar Perfil
              </button>
            )}

            <div className="border-t border-gray-100 my-1"></div>

            {user ? (
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth={1.5} 
                  stroke="currentColor" 
                  className="w-5 h-5 mr-3"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" 
                  />
                </svg>
                Sair
              </button>
            ) : (
              <button
                onClick={() => {
                  navigate('/login', { state: { from: location } });
                  setIsOpen(false);
                  if (onCloseMobileMenu) {
                    onCloseMobileMenu();
                  }
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-primary hover:bg-gray-100 transition-colors"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth={1.5} 
                  stroke="currentColor" 
                  className="w-5 h-5 mr-3"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" 
                  />
                </svg>
                Fazer Login
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal de Atualização de Perfil */}
      {showUpdateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Atualizar Perfil</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-primary focus:border-primary focus:border outline-none transition-colors"
                  placeholder="Digite seu nome completo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-primary focus:border-primary focus:border outline-none transition-colors"
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveProfile}
                className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-hover transition-colors"
              >
                Salvar
              </button>
              <button
                onClick={handleCancelUpdate}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
